/* eslint-disable react/require-default-props, react/forbid-prop-types */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
   FaFileExcel,
   FaUpload,
   FaCheck,
   FaChevronLeft,
   FaExclamationTriangle,
} from 'react-icons/fa';

import { PrimaryButton, GhostButton, SmallButton } from '../../../../../styles/ui/Button';
import ItemCard from './ItemCard';
import {
   ReviewCard,
   ReviewTop,
   ReviewHead,
   StatsRow,
   StatChip,
   Toolbar,
   Filters,
   FilterButton,
   SelectAllButton,
   CheckBox,
   List,
   EmptyList,
   ReviewFoot,
   ErrorBlock,
   ErrorRow,
} from './styles';
import {
   formatWeekdayShortBr,
   formatScheduleDateBr,
   formatSchedulePeriodLabel,
   baseLabel,
   rowHasGps,
   rowIsDuplicate,
} from './importHelpers';

const FILTER_ALL   = 'ALL';
const FILTER_VALID = 'VALID';
const FILTER_NOGPS = 'NOGPS';
const FILTER_DUP   = 'DUP';

// ===========================================================================
// BatchReview — revisão da planilha analisada em cards. Seleção mora no
// orquestrador (o confirm precisa dela); o filtro é local (só view). Erros
// aparecem num bloco compacto (o errorRow não traz campos de card).
// ===========================================================================
export default function BatchReview({
   previewData,
   target,
   clusterOrigins = [],
   selectedLineNumbers,
   canRestore = false,
   isConfirming = false,
   onToggleLine,
   onSetSelection,
   onReupload,
   onConfirm,
   onRequestRestore,
}) {
   const [filter, setFilter] = useState(FILTER_ALL);

   const validRows = useMemo(
      () => previewData.validRows || [],
      [previewData],
   );
   const warningRows = useMemo(
      () => previewData.warningRows || [],
      [previewData],
   );
   const errorRows = useMemo(
      () => previewData.errorRows || [],
      [previewData],
   );

   // Bloqueante vs não-bloqueante (decisão 2026-07-08): só travam a inserção
   // as linhas com CAMPO-CHAVE faltando (errorRows) e as DUPLICATAS (já existem
   // na data/período/base → 409). GPS ausente / janela ilegível NÃO bloqueiam —
   // a linha importa (só não roteiriza até geocodar), então conta como VÁLIDA.
   // Duplicata é categoria própria (bloqueante), fora dos "válidos".
   const duplicateRows = useMemo(
      () => warningRows.filter((r) => rowIsDuplicate(r)),
      [warningRows],
   );
   // Válidos = importáveis: os "puros" + os avisos não-bloqueantes (GPS etc.).
   const importableRows = useMemo(
      () => [...validRows, ...warningRows.filter((r) => !rowIsDuplicate(r))],
      [validRows, warningRows],
   );
   // Todos os cards (para o filtro "Todos") = válidos + duplicatas.
   const allCandidateRows = useMemo(
      () => [...validRows, ...warningRows],
      [validRows, warningRows],
   );
   // Subconjunto acionável dos válidos: importam, mas precisam de geocode.
   const noGpsRows = useMemo(
      () => importableRows.filter((r) => !rowHasGps(r)),
      [importableRows],
   );

   const stats = useMemo(() => ({
      validos: importableRows.length,
      semGps: noGpsRows.length,
      duplicados: duplicateRows.length,
      erros: errorRows.length,
   }), [importableRows, noGpsRows, duplicateRows, errorRows]);

   const shownRows = useMemo(() => {
      if (filter === FILTER_VALID) return importableRows;
      if (filter === FILTER_NOGPS) return noGpsRows;
      if (filter === FILTER_DUP) return duplicateRows;
      return allCandidateRows;
   }, [filter, importableRows, noGpsRows, duplicateRows, allCandidateRows]);

   // "Selecionar visíveis" só alcança linhas selecionáveis — duplicatas
   // (bloqueantes) ficam de fora, coerente com o card não-selecionável.
   const selectableVisible = shownRows
      .filter((r) => !rowIsDuplicate(r))
      .map((r) => r.lineNumber);
   const allVisibleSelected =
      selectableVisible.length > 0 &&
      selectableVisible.every((n) => selectedLineNumbers.has(n));

   const handleToggleAllVisible = () => {
      onSetSelection(selectableVisible, !allVisibleSelected);
   };

   const fileMeta =
      `${formatWeekdayShortBr(target.scheduleDate)} ` +
      `${formatScheduleDateBr(target.scheduleDate)} · ` +
      `${formatSchedulePeriodLabel(target.schedulePeriod)} · ` +
      `${baseLabel(clusterOrigins, target.clusterOrigin)}`;

   const selectedCount = selectedLineNumbers.size;
   const importableCount = importableRows.length;
   const totalCandidates = allCandidateRows.length;

   return (
      <ReviewCard>
         <ReviewTop>
            <span className="fic">
               <FaFileExcel size={17} />
            </span>
            <div className="file-txt">
               <div className="fn" title={target.fileName}>
                  {target.fileName || 'Planilha do Refiltek'}
               </div>
               <div className="fm">{fileMeta}</div>
            </div>
            <SmallButton
               type="button"
               className="rev-swap"
               onClick={onReupload}
               disabled={isConfirming}
            >
               <FaUpload size={13} /> Trocar planilha
            </SmallButton>
         </ReviewTop>

         <ReviewHead>
            <h2>Revisar e confirmar</h2>
            <p>
               Confira os itens e desmarque o que não quer importar. Só travam a
               inserção linhas com erro (campo-chave faltando) e duplicatas —
               &ldquo;sem GPS&rdquo; importa normalmente.
            </p>
         </ReviewHead>

         <StatsRow>
            <StatChip $variant="ok">
               <span className="dotc" /> <span className="n">{stats.validos}</span>{' '}
               válido(s)
            </StatChip>
            {stats.semGps > 0 && (
               <StatChip
                  $variant="warn"
                  title="Importáveis — só não roteirizáveis até geocodar"
               >
                  <span className="dotc" /> <span className="n">{stats.semGps}</span>{' '}
                  sem GPS
               </StatChip>
            )}
            {stats.duplicados > 0 && (
               <StatChip
                  $variant="err"
                  title="Já existem nesta data/período/base — não podem ser reimportados"
               >
                  <span className="dotc" />{' '}
                  <span className="n">{stats.duplicados}</span> duplicado(s)
               </StatChip>
            )}
            {stats.erros > 0 && (
               <StatChip $variant="err">
                  <span className="n">{stats.erros}</span> com erro
               </StatChip>
            )}
         </StatsRow>

         <Toolbar>
            <Filters>
               <FilterButton
                  type="button"
                  $active={filter === FILTER_ALL}
                  onClick={() => setFilter(FILTER_ALL)}
               >
                  Todos <span className="n">({totalCandidates})</span>
               </FilterButton>
               <FilterButton
                  type="button"
                  $active={filter === FILTER_VALID}
                  onClick={() => setFilter(FILTER_VALID)}
               >
                  Válidos <span className="n">({stats.validos})</span>
               </FilterButton>
               {stats.semGps > 0 && (
                  <FilterButton
                     type="button"
                     $active={filter === FILTER_NOGPS}
                     onClick={() => setFilter(FILTER_NOGPS)}
                  >
                     Sem GPS <span className="n">({stats.semGps})</span>
                  </FilterButton>
               )}
               {stats.duplicados > 0 && (
                  <FilterButton
                     type="button"
                     $active={filter === FILTER_DUP}
                     onClick={() => setFilter(FILTER_DUP)}
                  >
                     Duplicados <span className="n">({stats.duplicados})</span>
                  </FilterButton>
               )}
            </Filters>
            <SelectAllButton
               type="button"
               onClick={handleToggleAllVisible}
               disabled={selectableVisible.length === 0}
            >
               <CheckBox $sm $on={allVisibleSelected}>
                  {allVisibleSelected && <FaCheck size={11} />}
               </CheckBox>
               {allVisibleSelected ? 'Desmarcar visíveis' : 'Selecionar visíveis'}
            </SelectAllButton>
         </Toolbar>

         <List>
            {shownRows.length === 0 ? (
               <EmptyList>Nenhum item neste filtro.</EmptyList>
            ) : (
               shownRows.map((row) => (
                  <ItemCard
                     key={row.lineNumber}
                     row={row}
                     scheduleDate={target.scheduleDate}
                     schedulePeriod={target.schedulePeriod}
                     selected={selectedLineNumbers.has(row.lineNumber)}
                     disabled={rowIsDuplicate(row)}
                     canRestore={canRestore}
                     onToggle={onToggleLine}
                     onRequestRestore={onRequestRestore}
                  />
               ))
            )}
         </List>

         {filter === FILTER_ALL && errorRows.length > 0 && (
            <ErrorBlock>
               <div className="eh">
                  <FaExclamationTriangle size={12} /> Linhas com erro (
                  {errorRows.length}) — não importáveis
               </div>
               {errorRows.map((er) => (
                  <ErrorRow key={er.lineNumber}>
                     <span className="ln">Linha {er.lineNumber}</span>
                     {er.rawOrderNumber ? (
                        <span className="pn">{er.rawOrderNumber}</span>
                     ) : null}
                     <span className="msg">{er.error}</span>
                  </ErrorRow>
               ))}
            </ErrorBlock>
         )}

         <ReviewFoot>
            <div className="cnt">
               <b>{selectedCount}</b> item(ns) selecionado(s) de{' '}
               <b>{importableCount}</b> importáveis
            </div>
            <div className="acts">
               <GhostButton type="button" onClick={onReupload} disabled={isConfirming}>
                  <FaChevronLeft size={14} /> Voltar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={onConfirm}
                  disabled={isConfirming || selectedCount === 0}
               >
                  <FaCheck size={15} />
                  {isConfirming
                     ? 'Importando…'
                     : `Confirmar ${selectedCount} item(ns)`}
               </PrimaryButton>
            </div>
         </ReviewFoot>
      </ReviewCard>
   );
}

BatchReview.propTypes = {
   previewData: PropTypes.object.isRequired,
   // { scheduleDate, schedulePeriod, clusterOrigin, fileName }
   target: PropTypes.object.isRequired,
   clusterOrigins: PropTypes.array,
   selectedLineNumbers: PropTypes.instanceOf(Set).isRequired,
   canRestore: PropTypes.bool,
   isConfirming: PropTypes.bool,
   onToggleLine: PropTypes.func.isRequired,
   onSetSelection: PropTypes.func.isRequired,
   onReupload: PropTypes.func.isRequired,
   onConfirm: PropTypes.func.isRequired,
   onRequestRestore: PropTypes.func,
};
